import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BackwardArrow from '@jwp/ott-theme/assets/icons/arrow_backward.svg';
import NoResultsIcon from '@jwp/ott-theme/assets/icons/no_result.svg?react';

import type { UserNotification } from '@jwp/ott-common/types/notification';
import { groupNotifications, formatNotificationTime, formatNotificationDateHeader } from '../../utils/notificationGrouping';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';

import styles from './Notifications.module.scss';
import { useTheme } from '../../../../../ThemeContext';

type Props = {
  notifications: UserNotification[];
  onNotificationClick: (notification: UserNotification) => void;
  onClearAllClick: () => void;
};

const Notifications = ({ notifications, onNotificationClick, onClearAllClick }: Props): JSX.Element => {
  const { t, i18n } = useTranslation('user');
  const navigate = useNavigate();
  const { universalStyles } = useTheme();
  const grouped = groupNotifications(notifications);

  const handleBackClick = () => {
    navigate('/');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitlePart}>
          <IconButton className={styles.iconButton} aria-label={t('notifications.back')} onClick={handleBackClick}>
            <img src={BackwardArrow} alt="" />
          </IconButton>
          <h1 className={styles.title}>{t('notifications.title')}</h1>
        </div>
        {notifications.length > 0 && (
          <Button
            type="button"
            variant="dangerText"
            size="large"
            label={t('notifications.clear_all')}
            onClick={onClearAllClick}
            textStyle={universalStyles.deleteText}
          />
        )}
      </div>

      {notifications.length === 0 ? (
        <div className={styles.emptyState}>
          <NoResultsIcon className={styles.emptyStateIcon} aria-hidden="true" />
          <p className={styles.emptyStateText}>{t('notifications.empty')}</p>
        </div>
      ) : (
        grouped.map(({ id, isToday, notifications: groupNotifications }) => {
          const sectionLabel = isToday ? t('notifications.groups.today') : formatNotificationDateHeader(id, i18n.language);

          return (
          <section key={id} className={styles.groupCard} aria-label={sectionLabel}>
            <h2 className={styles.groupTitle}>{sectionLabel}</h2>
            <ul className={styles.notificationList}>
              {groupNotifications.map((notification, index) => (
                <li key={notification.id}>
                  {index > 0 && <hr className={styles.divider} aria-hidden="true" />}
                  <button
                    type="button"
                    className={styles.notificationItem}
                    onClick={() => onNotificationClick(notification)}
                  >
                    {notification.thumbnailUrl ? (
                      <img src={notification.thumbnailUrl} alt="" className={styles.thumbnail} />
                    ) : (
                      <div className={styles.thumbnailPlaceholder} aria-hidden="true" />
                    )}
                    <div className={styles.content}>
                      <p className={styles.message}>{notification.message}</p>
                      <p className={styles.timestamp}>{formatNotificationTime(notification.createdAt)}</p>
                    </div>
                    {!notification.isRead && <span className={styles.unreadDot} aria-label={t('notifications.unread')} />}
                  </button>
                </li>
              ))}
            </ul>
          </section>
          );
        })
      )}
    </div>
  );
};

export default Notifications;
